import { DocumentParseData } from "../../services/document-parse-data.service";
import {
  FileText,
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Activity,
  Database,
  Zap,
  Sparkles,
  TrendingUp,
  Package,
  Link,
  XCircle,
  Loader2,
} from "lucide-react";

interface LogsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  data: DocumentParseData;
  title: string;
}

export const LogsPopup: React.FC<LogsPopupProps> = ({
  isOpen,
  onClose,
  data,
  title,
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return "N/A";
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "done" || statusLower === "success") {
      return <CheckCircle className="w-4 h-4 text-emerald-600" />;
    } else if (statusLower === "pending" || statusLower === "processing") {
      return <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />;
    } else if (statusLower === "failed") {
      return <XCircle className="w-4 h-4 text-rose-600" />;
    } else {
      return <AlertCircle className="w-4 h-4 text-slate-600" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "done" || statusLower === "success") {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (statusLower === "pending" || statusLower === "processing") {
      return "bg-amber-50 text-amber-700 border-amber-200";
    } else if (statusLower === "failed") {
      return "bg-rose-50 text-rose-700 border-rose-200";
    } else {
      return "bg-slate-50 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Enhanced Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-6xl max-h-[90vh] animate-in zoom-in-95 duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-slate-200/60 overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-50 via-indigo-50/50 to-violet-50/50 border-b-2 border-slate-200/60 px-8 py-5 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-xl flex items-center justify-center shadow-sm ring-2 ring-indigo-200/50">
                  <Activity className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    {title}
                    <Sparkles className="w-4 h-4 text-violet-500" />
                  </h2>
                  <p className="text-sm text-slate-500 font-medium mt-0.5">
                    Complete processing details and metadata
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 hover:bg-rose-50 rounded-xl transition-all duration-200 border-2 border-slate-200 hover:border-rose-300 group"
                title="Close"
              >
                <X className="w-5 h-5 text-slate-600 group-hover:text-rose-600 transition-colors" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-br from-slate-50 to-indigo-50/30">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Document Parse Information */}
              <div className="bg-white rounded-xl  border-2 border-slate-200/60 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-6 py-4 border-b-2 border-slate-200/60">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    Parse Processing Information
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {/* Parse Status */}
                    <div className="space-y-3">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Parse Status
                      </p>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(data.document_parse_status)}
                        <span
                          className={`px-3 py-1.5 text-sm font-bold rounded-lg border-2 ${getStatusBadgeColor(
                            data.document_parse_status
                          )}`}
                        >
                          {data.document_parse_status || "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="space-y-3">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Duration
                      </p>
                      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-200">
                        <Zap className="w-4 h-4 text-indigo-600" />
                        <p className="text-base font-bold text-indigo-900">
                          {formatDuration(data.document_parse_duration)}
                        </p>
                      </div>
                    </div>

                    {/* Start Time */}
                    <div className="space-y-3">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Start Time
                      </p>
                      <p className="text-sm font-semibold text-slate-800 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                        {formatDate(data.document_parse_start_time)}
                      </p>
                    </div>

                    {/* End Time */}
                    <div className="space-y-3">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        End Time
                      </p>
                      <p className="text-sm font-semibold text-slate-800 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                        {formatDate(data.document_parse_end_time)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Data Details */}
              {data.file_data && data.file_data.length > 0 && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-violet-600" />
                      </div>
                      File Details
                    </h3>
                    {data.file_data.length > 1 && (
                      <span className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-bold border-2 border-indigo-200">
                        {data.file_data.length} Files
                      </span>
                    )}
                  </div>

                  {data.file_data.map((file, index) => (
                    <div
                      key={file._id || index}
                      className="bg-white rounded-xl  border-2 border-slate-200/60 overflow-hidden"
                    >
                      {/* File Header */}
                      <div className="bg-gradient-to-r from-slate-50 to-indigo-50/30 px-6 py-4 border-b-2 border-slate-200/60">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0 flex items-center gap-3">
                            {data.file_data.length > 1 && (
                              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-100 text-indigo-700 text-sm font-bold flex-shrink-0">
                                {index + 1}
                              </span>
                            )}
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-base font-bold text-slate-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-slate-500 font-mono mt-1 truncate">
                                ID: {file._id}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 whitespace-nowrap ${getStatusBadgeColor(
                              file.overall_processed_status
                            )}`}
                          >
                            {file.overall_processed_status}
                          </span>
                        </div>
                      </div>

                      <div className="p-6 space-y-5">
                        {/* File Properties */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-4 border-2 border-indigo-200/50">
                            <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">
                              File Type
                            </p>
                            <p className="text-sm font-bold text-indigo-900 uppercase">
                              {file.file_type}
                            </p>
                          </div>
                          <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-4 border-2 border-violet-200/50">
                            <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">
                              File Size
                            </p>
                            <p className="text-sm font-bold text-violet-900">
                              {(file.file_size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border-2 border-emerald-200/50">
                            <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">
                              Pages
                            </p>
                            <p className="text-sm font-bold text-emerald-900">
                              {file.file_pages}
                            </p>
                          </div>
                          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200/50">
                            <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">
                              Upload Type
                            </p>
                            <p className="text-sm font-bold text-amber-900">
                              {file.upload_type}
                            </p>
                          </div>
                        </div>

                        {/* Processing Type */}
                        <div className="bg-slate-50 rounded-xl p-4 border-2 border-slate-200">
                          <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">
                            Processing Type
                          </p>
                          <p className="text-sm font-bold text-slate-900">
                            {file.file_processing_type}
                          </p>
                        </div>

                        {/* Processing Status */}
                        <div className="bg-gradient-to-br from-slate-50 to-indigo-50/30 rounded-xl p-5 border-2 border-slate-200">
                          <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <Package className="w-4 h-4 text-indigo-600" />
                            Processing Status
                          </p>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white rounded-xl p-4 border-2 border-slate-200 hover:border-indigo-300 transition-colors">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  {getStatusIcon(file.visual_processing_status)}
                                  <span className="text-xs font-bold text-slate-700">
                                    Visual
                                  </span>
                                </div>
                                <span
                                  className={`text-xs font-bold px-2 py-1 rounded-lg ${getStatusBadgeColor(
                                    file.visual_processing_status
                                  )}`}
                                >
                                  {file.visual_processing_status}
                                </span>
                              </div>
                            </div>
                            <div className="bg-white rounded-xl p-4 border-2 border-slate-200 hover:border-violet-300 transition-colors">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  {getStatusIcon(file.rag_processing_status)}
                                  <span className="text-xs font-bold text-slate-700">
                                    RAG
                                  </span>
                                </div>
                                <span
                                  className={`text-xs font-bold px-2 py-1 rounded-lg ${getStatusBadgeColor(
                                    file.rag_processing_status
                                  )}`}
                                >
                                  {file.rag_processing_status}
                                </span>
                              </div>
                            </div>
                            <div className="bg-white rounded-xl p-4 border-2 border-slate-200 hover:border-emerald-300 transition-colors">
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                  {getStatusIcon(file.overall_processed_status)}
                                  <span className="text-xs font-bold text-slate-700">
                                    Overall
                                  </span>
                                </div>
                                <span
                                  className={`text-xs font-bold px-2 py-1 rounded-lg ${getStatusBadgeColor(
                                    file.overall_processed_status
                                  )}`}
                                >
                                  {file.overall_processed_status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Processing Times */}
                        {(file.visual_processing ||
                          file.rag_processing ||
                          file.overall_processing) && (
                          <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-5 border-2 border-indigo-200/50">
                            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-indigo-600" />
                              Processing Times
                            </p>
                            <div className="space-y-3">
                              {file.visual_processing && (
                                <div className="flex justify-between items-center bg-white rounded-xl p-4 border-2 border-slate-200">
                                  <span className="text-sm font-bold text-slate-600">
                                    Visual Processing:
                                  </span>
                                  <span className="text-sm font-bold text-indigo-900 bg-indigo-100 px-3 py-1 rounded-lg">
                                    {file.visual_processing.time_taken?.toFixed(
                                      2
                                    )}
                                    s
                                  </span>
                                </div>
                              )}
                              {file.rag_processing && (
                                <div className="flex justify-between items-center bg-white rounded-xl p-4 border-2 border-slate-200">
                                  <span className="text-sm font-bold text-slate-600">
                                    RAG Processing:
                                  </span>
                                  <span className="text-sm font-bold text-violet-900 bg-violet-100 px-3 py-1 rounded-lg">
                                    {file.rag_processing.time_taken?.toFixed(2)}
                                    s
                                  </span>
                                </div>
                              )}
                              {file.overall_processing && (
                                <div className="flex justify-between items-center bg-white rounded-xl p-4 border-2 border-emerald-200">
                                  <span className="text-sm font-bold text-slate-600">
                                    Total Time:
                                  </span>
                                  <span className="text-sm font-bold text-emerald-900 bg-emerald-100 px-3 py-1 rounded-lg">
                                    {file.overall_processing.time_taken?.toFixed(
                                      2
                                    )}
                                    s
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* File URL */}
                        {file.url && (
                          <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                            <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide flex items-center gap-2">
                              <Link className="w-4 h-4 text-blue-600" />
                              File URL
                            </p>
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700 font-semibold break-all hover:underline transition-colors"
                            >
                              {file.url}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* File IDs */}
              {data.file_ids && data.file_ids.length > 0 && (
                <div className="bg-white rounded-xl border-2 border-slate-200/60 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-6 py-4 border-b-2 border-slate-200/60">
                    <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Database className="w-5 h-5 text-indigo-600" />
                      Associated File IDs
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      {data.file_ids.map((fileId, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-gradient-to-r from-slate-50 to-indigo-50/30 rounded-xl border-2 border-slate-200 hover:border-indigo-300 transition-colors"
                        >
                          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-100 to-violet-100 text-indigo-700 text-xs font-bold">
                            #{index + 1}
                          </span>
                          <code className="text-sm font-mono text-slate-700 flex-1 font-semibold">
                            {fileId}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-white rounded-xl border-2 border-slate-200/60 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-6 py-4 border-b-2 border-slate-200/60">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                    Metadata
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-200">
                      <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">
                        Document ID
                      </p>
                      <code className="text-sm font-mono text-slate-900 break-all font-semibold">
                        {data._id}
                      </code>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-200">
                      <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">
                        Document Store ID
                      </p>
                      <code className="text-sm font-mono text-slate-900 break-all font-semibold">
                        {data.document_store_id}
                      </code>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-5 border-2 border-indigo-200">
                      <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">
                        Created At
                      </p>
                      <p className="text-sm font-bold text-indigo-900">
                        {formatDate(data.createdAt)}
                      </p>
                    </div>
                    <div className="bg-violet-50 rounded-xl p-5 border-2 border-violet-200">
                      <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">
                        Updated At
                      </p>
                      <p className="text-sm font-bold text-violet-900">
                        {formatDate(data.updatedAt)}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-5 border-2 border-slate-200">
                      <p className="text-xs font-bold text-slate-500 mb-3 uppercase tracking-wide">
                        Deleted Status
                      </p>
                      <span
                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-lg border-2 ${
                          data.is_deleted
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        {data.is_deleted ? (
                          <>
                            <XCircle className="w-4 h-4" />
                            Yes
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            No
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-5 bg-gradient-to-r from-slate-50 to-indigo-50/30 border-t-2 border-slate-200/60 flex-shrink-0 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
